/**
 * useComments Hook
 *
 * Custom hook for managing comments on a route.
 * Supports creating comments, replies, editing, and deletion.
 * Real-time subscription to comments for the route.
 */

import { useState, useEffect, useCallback } from 'react';
import {
  collection,
  doc,
  query,
  where,
  orderBy,
  onSnapshot,
  addDoc,
  updateDoc,
  deleteDoc,
  serverTimestamp,
  increment,
  type Unsubscribe,
} from 'firebase/firestore';
import { getFirebaseDb } from '@/lib/firebase';
import { useAuth } from '@/hooks/useAuth';
import type { RouteComment, CommentAuthor } from '@flash/shared';
import { filterContent, getBlockedContentMessage } from '@flash/shared';

/**
 * Comment with replies for display
 */
export interface CommentThread {
  comment: RouteComment;
  replies: RouteComment[];
}

/**
 * State interface for comments query
 */
interface UseCommentsState {
  /** All top-level comments with their replies */
  threads: CommentThread[];
  /** Whether data is being loaded */
  loading: boolean;
  /** Error message if query failed */
  error: string | null;
}

/**
 * Return type for useComments hook
 */
interface UseCommentsResult extends UseCommentsState {
  /** Add a new top-level comment */
  addComment: (text: string) => Promise<void>;
  /** Add a reply to an existing comment */
  addReply: (parentId: string, text: string) => Promise<void>;
  /** Edit an existing comment */
  editComment: (commentId: string, text: string) => Promise<void>;
  /** Delete a comment */
  deleteComment: (commentId: string) => Promise<void>;
  /** Whether a submission operation is in progress */
  isSubmitting: boolean;
  /** Total number of comments (including replies) */
  totalCount: number;
}

/**
 * Hook to manage comments for a specific route
 *
 * @param routeId - The route ID to manage comments for
 * @param gymId - The gym ID for the route (required for new comments)
 * @returns Object containing comments state and management functions
 */
export const useComments = (
  routeId: string | undefined,
  gymId: string | undefined
): UseCommentsResult => {
  const { user, userProfile } = useAuth();

  const [state, setState] = useState<UseCommentsState>({
    threads: [],
    loading: true,
    error: null,
  });

  const [isSubmitting, setIsSubmitting] = useState(false);

  // Real-time subscription to comments
  useEffect(() => {
    if (!routeId) {
      setState({ threads: [], loading: false, error: null });
      return;
    }

    setState((prev) => ({ ...prev, loading: true, error: null }));

    const db = getFirebaseDb();
    const commentsRef = collection(db, 'comments');
    const commentsQuery = query(
      commentsRef,
      where('routeId', '==', routeId),
      orderBy('createdAt', 'desc')
    );

    const unsubscribe: Unsubscribe = onSnapshot(
      commentsQuery,
      (snapshot) => {
        const allComments: RouteComment[] = [];

        snapshot.forEach((docSnapshot) => {
          const data = docSnapshot.data();
          const comment: RouteComment = {
            id: docSnapshot.id,
            routeId: data['routeId'] as string,
            gymId: data['gymId'] as string,
            author: data['author'] as CommentAuthor,
            text: data['text'] as string,
            parentId: (data['parentId'] as string) ?? null,
            replyCount: (data['replyCount'] as number) ?? 0,
            isEdited: (data['isEdited'] as boolean) ?? false,
            isDeleted: (data['isDeleted'] as boolean) ?? false,
            createdAt: data['createdAt'],
            updatedAt: data['updatedAt'],
          };
          allComments.push(comment);
        });

        // Organize into threads (top-level comments with replies)
        const topLevel = allComments.filter((c) => !c.parentId && !c.isDeleted);
        const replies = allComments.filter((c) => c.parentId && !c.isDeleted);

        const threads: CommentThread[] = topLevel.map((comment) => ({
          comment,
          replies: replies
            .filter((r) => r.parentId === comment.id)
            .sort((a, b) => {
              // Sort replies by createdAt ascending (oldest first)
              const aTime = a.createdAt && 'seconds' in a.createdAt ? a.createdAt.seconds : 0;
              const bTime = b.createdAt && 'seconds' in b.createdAt ? b.createdAt.seconds : 0;
              return aTime - bTime;
            }),
        }));

        setState({ threads, loading: false, error: null });
      },
      (err) => {
        const errorMessage = err instanceof Error ? err.message : 'Failed to fetch comments';
        console.error('Error in comments subscription:', err);
        setState((prev) => ({ ...prev, loading: false, error: errorMessage }));
      }
    );

    return () => {
      unsubscribe();
    };
  }, [routeId]);

  /**
   * Get the current user's author info
   */
  const getAuthorInfo = useCallback((): CommentAuthor => {
    if (!user) {
      throw new Error('Must be logged in to comment');
    }

    const photoURL = userProfile?.photoURL ?? user.photoURL;
    return {
      userId: user.uid,
      displayName: userProfile?.displayName ?? user.displayName ?? 'Anonymous',
      // Only include photoURL if it exists (Firestore doesn't accept undefined)
      ...(photoURL ? { photoURL } : {}),
    };
  }, [user, userProfile]);

  /**
   * Add a new top-level comment
   */
  const addComment = useCallback(
    async (text: string): Promise<void> => {
      if (!user) {
        throw new Error('Must be logged in to comment');
      }

      if (!routeId || !gymId) {
        throw new Error('Route ID and Gym ID are required');
      }

      const trimmedText = text.trim();
      if (!trimmedText || trimmedText.length > 500) {
        throw new Error('Comment must be 1-500 characters');
      }

      // Content filter - block inappropriate content
      const filterResult = filterContent(trimmedText);
      if (filterResult.flagged) {
        throw new Error(getBlockedContentMessage(filterResult.categories));
      }

      setIsSubmitting(true);

      try {
        const db = getFirebaseDb();
        const commentsRef = collection(db, 'comments');

        await addDoc(commentsRef, {
          routeId,
          gymId,
          author: getAuthorInfo(),
          text: trimmedText,
          parentId: null,
          replyCount: 0,
          isEdited: false,
          isDeleted: false,
          moderationStatus: 'visible',
          createdAt: serverTimestamp(),
          updatedAt: serverTimestamp(),
        });
      } finally {
        setIsSubmitting(false);
      }
    },
    [user, routeId, gymId, getAuthorInfo]
  );

  /**
   * Add a reply to an existing comment
   */
  const addReply = useCallback(
    async (parentId: string, text: string): Promise<void> => {
      if (!user) {
        throw new Error('Must be logged in to reply');
      }

      if (!routeId || !gymId) {
        throw new Error('Route ID and Gym ID are required');
      }

      const trimmedText = text.trim();
      if (!trimmedText || trimmedText.length > 500) {
        throw new Error('Reply must be 1-500 characters');
      }

      // Content filter - block inappropriate content
      const filterResult = filterContent(trimmedText);
      if (filterResult.flagged) {
        throw new Error(getBlockedContentMessage(filterResult.categories));
      }

      setIsSubmitting(true);

      try {
        const db = getFirebaseDb();
        const commentsRef = collection(db, 'comments');
        const parentRef = doc(db, 'comments', parentId);

        // Add the reply
        await addDoc(commentsRef, {
          routeId,
          gymId,
          author: getAuthorInfo(),
          text: trimmedText,
          parentId,
          replyCount: 0,
          isEdited: false,
          isDeleted: false,
          moderationStatus: 'visible',
          createdAt: serverTimestamp(),
          updatedAt: serverTimestamp(),
        });

        // Increment parent's reply count
        await updateDoc(parentRef, {
          replyCount: increment(1),
          updatedAt: serverTimestamp(),
        });
      } finally {
        setIsSubmitting(false);
      }
    },
    [user, routeId, gymId, getAuthorInfo]
  );

  /**
   * Edit an existing comment
   */
  const editComment = useCallback(
    async (commentId: string, text: string): Promise<void> => {
      if (!user) {
        throw new Error('Must be logged in to edit');
      }

      const trimmedText = text.trim();
      if (!trimmedText || trimmedText.length > 500) {
        throw new Error('Comment must be 1-500 characters');
      }

      // Content filter - block inappropriate content
      const filterResult = filterContent(trimmedText);
      if (filterResult.flagged) {
        throw new Error(getBlockedContentMessage(filterResult.categories));
      }

      setIsSubmitting(true);

      try {
        const db = getFirebaseDb();
        const commentRef = doc(db, 'comments', commentId);

        await updateDoc(commentRef, {
          text: trimmedText,
          isEdited: true,
          updatedAt: serverTimestamp(),
        });
      } finally {
        setIsSubmitting(false);
      }
    },
    [user]
  );

  /**
   * Delete a comment
   */
  const deleteComment = useCallback(
    async (commentId: string): Promise<void> => {
      if (!user) {
        throw new Error('Must be logged in to delete');
      }

      setIsSubmitting(true);

      try {
        const db = getFirebaseDb();
        const commentRef = doc(db, 'comments', commentId);

        // Hard delete the comment
        await deleteDoc(commentRef);
      } finally {
        setIsSubmitting(false);
      }
    },
    [user]
  );

  // Calculate total count
  const totalCount = state.threads.reduce((sum, thread) => sum + 1 + thread.replies.length, 0);

  return {
    ...state,
    addComment,
    addReply,
    editComment,
    deleteComment,
    isSubmitting,
    totalCount,
  };
};

export default useComments;
